import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogLabel,
} from "@reach/alert-dialog"
import React, { useContext, useRef, useState } from "react"
import { PrimaryButton } from "source/giving/donate/form/components/display/PrimaryButton"
import { StandardOrEmbeddedLoginForm } from "source/login/StandardOrEmbeddedLoginForm"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { useChurchCenterIframe } from "source/shared/hooks/useChurchCenterIframe"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import { pushBrowserHistoryState } from "./BrowserNavigationHandler"
import useProductAnalyticsPublishEvent from "source/shared/hooks/useProductAnalyticsPublishEvent"
import { isChurchCenterApp } from "source/Layout"

interface ContinueButtonProps {
  authenticated: boolean
  validateProgress: () => boolean
  desktop: boolean
}

export function ContinueButton({
  authenticated,
  validateProgress,
  desktop,
}: ContinueButtonProps) {
  const text = "Continue to give"

  return authenticated ? (
    <ContinueToPaymentButton {...{ text, validateProgress }} />
  ) : (
    <ContinueToLoginButton {...{ text, validateProgress, desktop }} />
  )
}

interface InnerButtonProps {
  text: React.ReactNode
  validateProgress: () => boolean
  desktop?: boolean
}

function ContinueToLoginButton({
  text,
  validateProgress,
  desktop,
}: InnerButtonProps) {
  const { login } = useContext(ServerRenderedProps)
  const { dispatch } = useDonationFormContext()
  const { close: closeIframe, inIframe } = useChurchCenterIframe()
  const [showDialog, setShowDialog] = useState(false)
  const ref = useRef(null)
  const publishEvent = useProductAnalyticsPublishEvent()
  const isCCA = isChurchCenterApp()

  const handleClick = () => {
    const isValid = validateProgress()
    if (isValid) {
      setShowDialog(true)
      publishEvent("giving.donation-form.login-initiated", {
        source: isCCA ? "CCA" : desktop ? "CCW Desktop" : "CCW Mobile",
      })
    }
  }

  const handleLoginSuccess = () => {
    setShowDialog(false)
    dispatch({ type: "SET_FOCUSED_PANE", payload: "right" })
    pushBrowserHistoryState("right")
    publishEvent("giving.donation-form.login-successful", {
      source: isCCA ? "CCA" : desktop ? "CCW Desktop" : "CCW Mobile",
    })
  }

  const handleDismiss = () => {
    if (inIframe) {
      closeIframe()
    } else {
      setShowDialog(false)
    }
  }

  return (
    <>
      <PrimaryButton onClick={handleClick}>{text}</PrimaryButton>
      {showDialog && (
        <AlertDialog
          id="login-dialog"
          className="pc-donation-form"
          leastDestructiveRef={ref}
          onDismiss={handleDismiss}
        >
          <AlertDialogLabel></AlertDialogLabel>
          <div ref={ref}>
            <AlertDialogDescription>
              <StandardOrEmbeddedLoginForm
                emailOnly={login.email_only}
                onSuccess={handleLoginSuccess}
                textOverrides={{
                  devicePrompt: {
                    phone: "Enter your phone number",
                    email: "Enter your email address",
                  },
                  codeDescription:
                    "We\u2019ll send you a code to complete your donation securely.",
                  secondDevicePrompt: {
                    email: "To send your receipt,",
                  },
                  newPersonPrompt:
                    "Enter your first and last name to select your payment method.",
                  confirmPerson:
                    "Continue to select your payment method and complete your donation.",
                }}
              />
            </AlertDialogDescription>
          </div>
        </AlertDialog>
      )}
    </>
  )
}

function ContinueToPaymentButton({ text, validateProgress }: InnerButtonProps) {
  const { dispatch } = useDonationFormContext()

  const handleClick = () => {
    const isValid = validateProgress()
    if (isValid) {
      dispatch({ type: "SET_FOCUSED_PANE", payload: "right" })
      pushBrowserHistoryState("right")
    }
  }

  return <PrimaryButton onClick={handleClick}>{text}</PrimaryButton>
}
